import { useNavigate } from 'react-router-dom';
import { User, Home, Building, Lock, Shield, FileCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BrandedHeader } from './BrandedHeader';
import { ComplianceFooter } from './ComplianceFooter';
import { SecureUploadCompanyProfileStatus } from './SecureUploadCompanyProfileStatus';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';

export const SecureUploadLanding = () => {
  const navigate = useNavigate();
  const { companyProfile, loading, error, retry } = useCompanyProfile();

  if (loading || error || !companyProfile) {
    return <SecureUploadCompanyProfileStatus loading={loading} onRetry={retry} />;
  }

  const entityTypes = [
    {
      id: 'recruit',
      title: 'Recruit',
      icon: <User className="h-12 w-12" />,
      description: 'Upload onboarding documents',
      path: '/secureuploads/recruit',
      disabled: false
    },
    {
      id: 'borrower',
      title: 'Borrower',
      icon: <Home className="h-12 w-12" />,
      description: 'Upload loan documents to your loan officer',
      path: '/secureuploads/borrower',
      disabled: false
    },
    {
      id: 'escrow',
      title: 'Escrow/Title',
      icon: <Building className="h-12 w-12" />,
      description: 'Upload closing documents',
      path: '/secureuploads/escrow',
      disabled: true
    }
  ];

  const handleEntityClick = (entity: typeof entityTypes[0]) => {
    if (entity.disabled) {
return;
}
    if (entity.path) {
      navigate(entity.path);
    }
  };

  return (
    <div className="h-screen overflow-y-auto relative bg-gradient-to-br from-gray-50 via-blue-50/30 to-cyan-50/20 flex flex-col">
      {/* Decorative gradient orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-400/15 to-cyan-500/15 rounded-full blur-3xl pointer-events-none" />

      {/* Branded Header */}
      <BrandedHeader
        title="Secure Document Upload Portal"
        subtitle="Upload your documents securely with end-to-end encryption"
        companyProfile={companyProfile}
      />

      {/* Main Content */}
      <div className="relative flex-1 flex items-center justify-center p-8">
        <div className="max-w-6xl w-full">
          {/* Page Title & Description */}
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-cyan-900 to-blue-900 bg-clip-text text-transparent mb-4">
              Select Your Document Category
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Choose the appropriate category below to begin uploading your documents. All files are encrypted and transmitted securely.
            </p>
          </div>

          {/* Entity Type Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {entityTypes.map((entity, index) => (
              <button
                key={entity.id}
                disabled={entity.disabled}
                onClick={() => handleEntityClick(entity)}
                className={`group relative p-10 rounded-2xl transition-all duration-300 overflow-hidden
                  ${entity.disabled
                    ? 'bg-gray-100 border border-gray-200 cursor-not-allowed opacity-60'
                    : 'glass-card glass-card-hover active:scale-95 cursor-pointer'
                  }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Gradient overlay on hover */}
                {!entity.disabled && (
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                )}

                {/* Animated border glow */}
                {!entity.disabled && (
                  <div className="absolute inset-0 rounded-2xl ring-2 ring-cyan-400/0 group-hover:ring-cyan-400/50 ring-offset-2 ring-offset-white/50 transition-all duration-300" />
                )}

                <div className="relative flex flex-col items-center text-center space-y-6">
                  {/* Icon with glow effect */}
                  <div className={`transition-all duration-300
                    ${entity.disabled
                      ? 'text-gray-400'
                      : 'text-cyan-600 group-hover:scale-110 group-hover:glow-cyan'
                    }`}>
                    {entity.icon}
                  </div>

                  {/* Title with gradient on hover */}
                  <h3 className={`font-bold text-xl transition-all duration-300
                    ${entity.disabled
                      ? 'text-gray-500'
                      : 'text-gray-900 group-hover:bg-gradient-to-r group-hover:from-cyan-600 group-hover:to-blue-600 group-hover:bg-clip-text group-hover:text-transparent'
                    }`}>
                    {entity.title}
                  </h3>

                  {/* Description */}
                  <p className={`text-sm
                    ${entity.disabled
                      ? 'text-gray-400'
                      : 'text-gray-600 group-hover:text-gray-700'
                    }`}>
                    {entity.description}
                  </p>

                  {/* Coming Soon Badge */}
                  {entity.disabled && (
                    <Badge variant="secondary" className="mt-2 bg-gray-200 text-gray-600 border-gray-300">
                      Coming Soon
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Security Features Section */}
          <Card className="relative overflow-hidden glass-card border border-white/40 shadow-2xl shadow-blue-900/10">
            {/* Gradient background overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/90 to-blue-50/80 pointer-events-none" />

            <CardHeader className="relative">
              <CardTitle className="text-2xl font-bold flex items-center gap-3 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                <div className="relative">
                  <Shield className="h-6 w-6 text-cyan-600 animate-pulse-subtle" />
                  <div className="absolute inset-0 bg-cyan-400/30 rounded-full blur-md animate-glow-pulse" />
                </div>
                Security & Privacy
              </CardTitle>
              <CardDescription className="text-gray-700">
                Your documents are protected with industry-leading security measures
              </CardDescription>
            </CardHeader>

            <CardContent className="relative">
              <div className="grid md:grid-cols-3 gap-8">
                {/* SSL/TLS Encryption */}
                <div className="group flex items-start gap-4">
                  <div className="relative flex-shrink-0">
                    <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 p-3 rounded-xl border border-green-500/30 group-hover:border-green-500/50 transition-colors">
                      <Lock className="h-6 w-6 text-green-600 group-hover:scale-110 transition-transform" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-base text-gray-900 mb-2 group-hover:text-green-700 transition-colors">
                      256-bit SSL/TLS Encryption
                    </h4>
                    <p className="text-sm text-gray-600">
                      All data is encrypted during transmission using bank-level security
                    </p>
                  </div>
                </div>

                {/* Secure Storage */}
                <div className="group flex items-start gap-4">
                  <div className="relative flex-shrink-0">
                    <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 p-3 rounded-xl border border-blue-500/30 group-hover:border-blue-500/50 transition-colors">
                      <Shield className="h-6 w-6 text-blue-600 group-hover:scale-110 transition-transform" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-base text-gray-900 mb-2 group-hover:text-blue-700 transition-colors">
                      Secure Storage
                    </h4>
                    <p className="text-sm text-gray-600">
                      Documents stored in compliance with federal privacy regulations
                    </p>
                  </div>
                </div>

                {/* Identity Verification */}
                <div className="group flex items-start gap-4">
                  <div className="relative flex-shrink-0">
                    <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 p-3 rounded-xl border border-cyan-500/30 group-hover:border-cyan-500/50 transition-colors">
                      <FileCheck className="h-6 w-6 text-cyan-600 group-hover:scale-110 transition-transform" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-base text-gray-900 mb-2 group-hover:text-cyan-700 transition-colors">
                      Identity Verification
                    </h4>
                    <p className="text-sm text-gray-600">
                      NMLS number and date of birth required for secure access
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Compliance Footer */}
      <ComplianceFooter companyProfile={companyProfile} />
    </div>
  );
};
